import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { INVENTORY } from '@/constants/testIds';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, MagnifyingGlass, PencilSimple, Trash, Warning } from '@phosphor-icons/react';

export default function InventoryPage() {
  const [medicines, setMedicines] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editMed, setEditMed] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [medsRes, catsRes] = await Promise.all([api.getMedicines(search, category), api.getCategories()]);
      setMedicines(medsRes.data);
      setCategories(catsRes.data);
    } catch {}
    setLoading(false);
  }, [search, category]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (window.confirm('Delete this medicine?')) {
      await api.deleteMedicine(id);
      load();
    }
  };

  return (
    <div data-testid={INVENTORY.container} className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Inventory</h1>
          <p className="text-sm mt-1" style={{ color: '#8A94A6' }}>{medicines.length} medicines in stock</p>
        </div>
        <Button data-testid={INVENTORY.addMedicineBtn} onClick={() => { setEditMed(null); setShowForm(true); }}
          className="h-9 px-4 text-sm font-semibold rounded-md" style={{ background: '#0A2540', color: 'white' }}>
          <Plus size={16} weight="bold" className="mr-1" /> Add Medicine
        </Button>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8A94A6' }} />
          <Input data-testid={INVENTORY.searchInput} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search medicines..." className="pl-10 h-9" />
        </div>
        <Select data-testid={INVENTORY.categoryFilter} value={category} onValueChange={v => setCategory(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48 h-9"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div data-testid={INVENTORY.medicineTable} className="rounded-md border overflow-x-auto" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <thead>
            <tr style={{ background: '#F9F9FA', borderBottom: '1px solid #E2E4E9' }}>
              {['Medicine', 'Generic', 'Manufacturer', 'Category', 'MRP', 'Stock', 'Batch', 'Expiry', 'Actions'].map(h => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>Loading...</td></tr>
            ) : medicines.map(m => (
              <tr key={m.id} className="hover:bg-slate-50 transition-colors" style={{ borderBottom: '1px solid #E2E4E9' }}>
                <td className="px-3 py-2 font-medium" style={{ color: '#0A2540' }}>{m.name}</td>
                <td className="px-3 py-2 text-xs" style={{ color: '#8A94A6' }}>{m.generic_name}</td>
                <td className="px-3 py-2 text-xs" style={{ color: '#8A94A6' }}>{m.manufacturer}</td>
                <td className="px-3 py-2"><span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: '#F0F4F8', color: '#0A2540' }}>{m.category}</span></td>
                <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{m.mrp}</td>
                <td className="px-3 py-2 text-right">
                  <span className="flex items-center justify-end gap-1" style={{ color: m.stock < 50 ? '#FF3B30' : '#0A2540', fontVariantNumeric: 'tabular-nums' }}>
                    {m.stock < 50 && <Warning size={12} weight="fill" style={{ color: '#FF3B30' }} />}
                    {m.stock}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs" style={{ color: '#8A94A6' }}>{m.batch_number}</td>
                <td className="px-3 py-2 text-xs" style={{ color: '#8A94A6' }}>{m.expiry_date}</td>
                <td className="px-3 py-2 flex gap-1">
                  <button onClick={() => { setEditMed(m); setShowForm(true); }} className="p-1 rounded hover:bg-slate-100 transition-colors"><PencilSimple size={14} style={{ color: '#007AFF' }} /></button>
                  <button onClick={() => handleDelete(m.id)} className="p-1 rounded hover:bg-red-50 transition-colors"><Trash size={14} style={{ color: '#FF3B30' }} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MedicineFormDialog open={showForm} onClose={() => setShowForm(false)} medicine={editMed} onSaved={load} />
    </div>
  );
}

function MedicineFormDialog({ open, onClose, medicine, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm(medicine || { name: '', generic_name: '', manufacturer: '', sku: '', barcode: '', category: 'Analgesic', unit: 'Strip', pack_size: 10, mrp: 0, dealer_price: 0, gst_percent: 5, stock: 0, batch_number: '', expiry_date: '', rack_location: '' });
  }, [medicine, open]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSave = async () => {
    try {
      if (medicine?.id) await api.updateMedicine(medicine.id, form);
      else await api.createMedicine(form);
      onSaved();
      onClose();
    } catch (e) { console.error(e); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{medicine ? 'Edit Medicine' : 'Add Medicine'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {[
            { k: 'name', label: 'Name', span: 2 },
            { k: 'generic_name', label: 'Generic Name' },
            { k: 'manufacturer', label: 'Manufacturer' },
            { k: 'category', label: 'Category' },
            { k: 'unit', label: 'Unit' },
            { k: 'mrp', label: 'MRP', type: 'number' },
            { k: 'dealer_price', label: 'Dealer Price', type: 'number' },
            { k: 'gst_percent', label: 'GST %', type: 'number' },
            { k: 'stock', label: 'Stock', type: 'number' },
            { k: 'batch_number', label: 'Batch No.' },
            { k: 'expiry_date', label: 'Expiry', type: 'date' },
            { k: 'barcode', label: 'Barcode' },
            { k: 'sku', label: 'SKU' },
          ].map(f => (
            <div key={f.k} className={f.span === 2 ? 'col-span-2' : ''}>
              <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{f.label}</Label>
              <Input type={f.type || 'text'} value={form[f.k] || ''} onChange={e => update(f.k, f.type === 'number' ? Number(e.target.value) : e.target.value)} className="h-8 mt-1 text-sm" />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="h-8 text-sm">Cancel</Button>
          <Button onClick={handleSave} className="h-8 text-sm" style={{ background: '#0A2540', color: 'white' }}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
